import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2, Home as HomeIcon, CheckCircle2, ClipboardList, CalendarCheck,
  Sparkles, ShieldCheck, Clock, Star, ArrowRight, Phone,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { COMPANY, IMAGES, TESTIMONIALS, FAQS, LOGO_URL } from "../lib/constants";
import { SectionHeading, ServiceAreaSection, CTABand, fadeUp, Eyebrow } from "../components/common";

const benefits = [
  { icon: ShieldCheck, title: "Locally Operated", desc: "A trusted Northwest Arkansas team you'll recognize every visit." },
  { icon: Sparkles, title: "Attention to Detail", desc: "Consistent, thorough cleaning with checklists that never skip a step." },
  { icon: Clock, title: "Reliable & On Time", desc: "Dependable schedules for busy businesses and households alike." },
  { icon: CheckCircle2, title: "Custom Plans", desc: "Flexible commercial programs and easy online residential booking." },
];

const process = [
  { icon: ClipboardList, title: "Tell us your needs", desc: "Request a commercial quote or choose a residential service online." },
  { icon: CalendarCheck, title: "We build your plan", desc: "Custom schedule and pricing for commercial; instant booking for residential." },
  { icon: Sparkles, title: "We clean, you relax", desc: "Our professional team delivers spotless, consistent results." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative grain-dark overflow-hidden">
        <div className="ubm-container py-16 sm:py-20 lg:py-24 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }} className="max-w-xl">
            <div className="flex items-center gap-3 mb-5">
              <img src={LOGO_URL} alt="UBM logo" className="h-14 w-14 object-contain" />
              <div className="eyebrow !mb-0 leading-tight">United Building Maintenance<br />and Residential Cleaning Services</div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">
              Reliable Commercial and Residential Cleaning Across {COMPANY.region}
            </h1>
            <p className="mt-6 text-lg text-gray-300 leading-relaxed">
              From offices and medical facilities to family homes, our locally operated team delivers
              dependable, detail-focused cleaning you can count on.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/commercial#quote" className="inline-flex items-center justify-center gap-2 bg-primary text-white hover:bg-primary-hover font-semibold px-7 py-4 rounded-md transition-colors" data-testid="hero-commercial-btn">
                Request a Commercial Cleaning Quote <ArrowRight size={18} />
              </Link>
              <Link to="/book" className="inline-flex items-center justify-center gap-2 bg-white text-charcoal-900 hover:bg-gray-100 font-semibold px-7 py-4 rounded-md transition-colors" data-testid="hero-residential-btn">
                Book a Residential Cleaning <ArrowRight size={18} />
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-2 text-sm text-gray-400">
              <Phone size={15} className="text-primary" /> Speak with us: <a href={COMPANY.phoneLink} className="text-white font-medium hover:text-primary">{COMPANY.phoneDisplay}</a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
            <div className="absolute -inset-3 bg-primary/10 rounded-lg blur-2xl" />
            <img src={IMAGES.lobby} alt="United Building Maintenance office with UBM wall sign"
              className="relative w-full h-[340px] sm:h-[440px] lg:h-[520px] object-cover rounded-md border border-charcoal-700 shadow-2xl" />
          </motion.div>
        </div>
      </section>

      {/* TWO SERVICE PATHS */}
      <section className="bg-white" data-testid="service-paths">
        <div className="ubm-container py-20">
          <SectionHeading center eyebrow="Two ways we help" title="Choose your cleaning path"
            subtitle="Whether you manage a commercial property or run a household, we have a dedicated service built for you." />
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div {...fadeUp} className="group relative overflow-hidden rounded-md border border-gray-200 bg-charcoal-900 text-white">
              <img src={IMAGES.commercialFloor} alt="Commercial cleaning" className="h-56 w-full object-cover opacity-70 group-hover:opacity-90 transition-opacity" />
              <div className="p-8">
                <div className="flex items-center gap-3 mb-3">
                  <Building2 className="text-primary" />
                  <h3 className="text-2xl font-bold font-display">Commercial Cleaning</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  For businesses, offices, commercial buildings, property managers, medical facilities,
                  churches, retail spaces, and other commercial properties.
                </p>
                <Link to="/commercial#quote" className="mt-6 inline-flex items-center gap-2 bg-primary text-white hover:bg-primary-hover font-semibold px-6 py-3 rounded-md transition-colors" data-testid="path-commercial-btn">
                  Request a Commercial Cleaning Quote <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="group relative overflow-hidden rounded-md border border-gray-200 bg-white">
              <img src={IMAGES.residential} alt="Residential cleaning" className="h-56 w-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
              <div className="p-8">
                <div className="flex items-center gap-3 mb-3">
                  <HomeIcon className="text-primary" />
                  <h3 className="text-2xl font-bold font-display text-charcoal-900">Residential Cleaning</h3>
                </div>
                <p className="text-charcoal-600 leading-relaxed">
                  For homeowners who need recurring cleaning, deep cleaning, move-in cleaning,
                  move-out cleaning, or a one-time cleaning.
                </p>
                <Link to="/book" className="mt-6 inline-flex items-center gap-2 bg-charcoal-900 text-white hover:bg-charcoal-800 font-semibold px-6 py-3 rounded-md transition-colors" data-testid="path-residential-btn">
                  Book a Residential Cleaning <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-[#f8f9fa]">
        <div className="ubm-container py-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <img src={IMAGES.officeInterior} alt="Modern office interior" className="rounded-md border border-gray-200 w-full object-cover h-[380px]" />
          </motion.div>
          <motion.div {...fadeUp} transition={{ delay: 0.1 }}>
            <Eyebrow>Who we are</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold text-charcoal-900 tracking-tight">
              An established, trustworthy cleaning company for {COMPANY.region}
            </h2>
            <p className="mt-4 text-charcoal-600 leading-relaxed">
              United Building Maintenance and Residential Cleaning Services is locally operated by {COMPANY.owners}.
              We combine professional commercial janitorial expertise with friendly, dependable residential
              cleaning — all backed by clear communication and a genuine commitment to our neighbors.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {benefits.map((b) => (
                <div key={b.title} className="flex items-start gap-3">
                  <b.icon className="text-primary shrink-0 mt-0.5" size={20} />
                  <div>
                    <div className="font-semibold text-charcoal-900">{b.title}</div>
                    <div className="text-sm text-charcoal-600">{b.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-white">
        <div className="ubm-container py-20">
          <SectionHeading center eyebrow="How it works" title="A simple, proven cleaning process" />
          <div className="grid md:grid-cols-3 gap-8">
            {process.map((p, i) => (
              <motion.div key={p.title} {...fadeUp} transition={{ delay: i * 0.1 }} className="relative border border-gray-200 rounded-md p-8 hover:border-primary transition-colors">
                <div className="text-5xl font-bold font-display text-gray-100 absolute top-4 right-5">{i + 1}</div>
                <p.icon className="text-primary mb-4" size={30} />
                <h3 className="text-xl font-semibold text-charcoal-900">{p.title}</h3>
                <p className="mt-2 text-charcoal-600">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#f8f9fa]" data-testid="testimonials">
        <div className="ubm-container py-20">
          <SectionHeading center eyebrow="What clients say" title="Trusted by businesses and homeowners" />
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="bg-white border border-gray-200 rounded-md p-7 shadow-sm">
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, k) => <Star key={k} size={16} className="fill-primary text-primary" />)}
                </div>
                <p className="text-charcoal-700 leading-relaxed">"{t.text}"</p>
                <div className="mt-4 text-sm font-semibold text-charcoal-900">{t.name}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ServiceAreaSection />

      {/* FAQ */}
      <section className="bg-[#f8f9fa]" data-testid="faq">
        <div className="ubm-container py-20 max-w-3xl">
          <SectionHeading center eyebrow="FAQ" title="Frequently asked questions" />
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white border border-gray-200 rounded-md px-5">
                <AccordionTrigger className="text-left font-semibold text-charcoal-900" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-charcoal-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
